import { useEffect, useMemo, useRef, useState } from 'react'
import { standardName } from '../../../utils/standardRef'
import { useAuth } from '../../../context/AuthContext'
import { useModal } from '../../../context/ModalContext'
import { IconFilter } from '../../../layout/NavRail/NavRail'
import { useComments } from '../../../hooks/useComments'
import { useTopics } from '../../../hooks/useTopics'
import { CommentCard } from './CommentCard'
import { CommentComposer } from './CommentComposer'
import CommentsFilterPopover from './CommentsFilterPopover'
import {
  authorsOf,
  isLikedBy,
  isSavedBy,
  itemsToOptions,
  itemTreeToOptions,
  sortComments,
  taggedItemsOf,
} from './commentUtils'
import {
  COMMENT_SORTS,
  emptyCommentFilter,
  loadCommentFilter,
  makeCommentMatcher,
  saveCommentFilter,
} from './commentFilter'
import './Comments.css'

// Placeholder names the items the comment will be tagged to, not the section
// that happens to be open in the window.
function commentPlaceholder(items) {
  const labels = (items || []).map((it) => it.label).filter(Boolean)
  if (!labels.length) return 'Write a comment…'
  if (labels.length === 1) return `Comment on ${labels[0]}…`
  if (labels.length === 2) return `Comment on ${labels[0]} and ${labels[1]}…`
  return `Comment on ${labels[0]} and ${labels.length - 1} more…`
}

// Turn the open item into a picker option so a new comment defaults to it.
function itemToOption(item) {
  if (!item?._id) return null
  return {
    _id: item._id,
    label: item.label,
    standard: item.standard,
    order: item.order,
    path: item.path || [],
    pageStart: item.pageStart,
    pageEnd: item.pageEnd,
  }
}

export function CommentsPane({ item, items, windowId, onOpenItem, locate, onLocated }) {
  const { user } = useAuth()
  const { openModal } = useModal()
  const { topics: myTopics } = useTopics()
  const {
    comments,
    status,
    add,
    update,
    updateItems,
    updateTopics,
    remove,
    toggleLike,
    toggleSave,
    repliesByParent,
    loadReplies,
    addReply,
    reload,
  } = useComments(item)

  // The whole filter (attrs / authors / items / descendants / match mode / sort)
  // is one serializable blob, persisted per grid window in localStorage.
  const [filter, setFilter] = useState(() => loadCommentFilter(windowId))
  const [filterAnchor, setFilterAnchor] = useState(null)
  const [openReplies, setOpenReplies] = useState({})
  const [composeItems, setComposeItems] = useState([])

  const onLocatedRef = useRef(onLocated)
  onLocatedRef.current = onLocated
  // Set when this locate had to clear the filter, so the scroll waits one
  // render for the comment to actually be in the list.
  const locateCleared = useRef(null)
  const locateSawClear = useRef(null)
  const locateRetried = useRef(null)

  // Persist this window's filter whenever it changes.
  useEffect(() => {
    saveCommentFilter(windowId, filter)
  }, [windowId, filter])

  // Reset the transient reply-open state and default the composer's tag to the
  // newly opened item. The filter itself intentionally persists across items.
  useEffect(() => {
    setOpenReplies({})
    const opt = itemToOption(item)
    setComposeItems(opt ? [opt] : [])
  }, [item?._id, item?.order])

  const patchFilter = (partial) => setFilter((prev) => ({ ...prev, ...partial }))
  // Clear all facets but keep the match mode and sort (mirrors the nav popover).
  const clearFilter = () =>
    setFilter((prev) => ({ ...emptyCommentFilter(), matchMode: prev.matchMode, sort: prev.sort }))

  const authors = useMemo(() => authorsOf(comments), [comments])
  // Filter options come from the standard's full item tree (any chapter/section
  // can be picked, then rolled down via "include descendants"). Falls back to
  // the set of already-tagged items while the tree is still loading.
  const itemOptions = useMemo(() => {
    const treeOptions = itemTreeToOptions(items)
    return treeOptions.length ? treeOptions : taggedItemsOf(comments, item?.standard)
  }, [items, comments, item?.standard])

  // The user's own topics, plus any topic already on a loaded comment (a public
  // comment can carry someone else's topic).
  const topicOptions = useMemo(() => {
    const map = new Map()
    for (const t of myTopics) {
      if (t?._id) map.set(String(t._id), { _id: t._id, name: t.name })
    }
    for (const c of comments) {
      for (const t of c.topics || []) {
        if (t?._id && !map.has(String(t._id))) {
          map.set(String(t._id), { _id: t._id, name: t.name || 'Topic' })
        }
      }
    }
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [myTopics, comments])

  const matcher = useMemo(() => makeCommentMatcher(filter, user?._id), [filter, user])
  const visible = useMemo(
    () => sortComments(comments.filter(matcher.match), filter.sort),
    [comments, matcher, filter.sort]
  )

  // A page tag asked for this comment. Drop facets only when they would hide
  // it. Sort and match mode stay as the reader set them.
  useEffect(() => {
    if (!locate || locate.kind !== 'comment' || status !== 'ready') return
    if (locateCleared.current === locate.n) return
    const comment = comments.find((c) => String(c._id) === String(locate.id))
    if (comment && matcher.match(comment)) return
    locateCleared.current = locate.n
    setFilterAnchor(null)
    setFilter((prev) => ({ ...emptyCommentFilter(), matchMode: prev.matchMode, sort: prev.sort }))
  }, [locate, status, comments, matcher])

  useEffect(() => {
    if (!locate || locate.kind !== 'comment') return
    const el = document.getElementById(`cmt-${locate.id}`)
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el.classList.add('is-located')
      window.setTimeout(() => el.classList.remove('is-located'), 1600)
      onLocatedRef.current?.()
      return
    }
    if (status !== 'ready') return
    if (locateCleared.current === locate.n && locateSawClear.current !== locate.n) {
      locateSawClear.current = locate.n
      return
    }
    if (locateRetried.current === locate.n) {
      onLocatedRef.current?.()
      return
    }
    locateRetried.current = locate.n
    reload()
  }, [locate, visible, status, reload])

  const toggleFilter = (event) => {
    const button = event.currentTarget
    setFilterAnchor((current) => (current ? null : button))
  }

  const toggleReplies = async (id) => {
    const next = !openReplies[id]
    setOpenReplies((prev) => ({ ...prev, [id]: next }))
    if (next && repliesByParent[id] == null) await loadReplies(id)
  }

  const handleRemove = (comment) => {
    openModal('confirm', {
      title: comment.parent ? 'Delete reply' : 'Delete comment',
      message: 'This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => remove(comment._id),
    })
  }

  const editComposeItems = () => {
    openModal('comment-items', {
      selected: composeItems,
      standard: item?.standard || '',
      onSave: (picked) => setComposeItems(picked),
    })
  }

  const editCommentItems = (comment) => {
    openModal('comment-items', {
      selected: itemsToOptions(comment),
      standard: item?.standard || '',
      onSave: (picked) => updateItems(comment._id, picked),
    })
  }

  const editCommentTopics = (comment) => {
    openModal('comment-topics', {
      selected: (comment.topics || []).map((t) => ({ _id: t._id, name: t.name })),
      onSave: (picked) => updateTopics(comment._id, picked),
    })
  }

  const showLikes = (comment) => {
    openModal('comment-likes', { commentId: comment._id })
  }

  return (
    <div className="code-comments">
      <div className="cmt-toolbar">
        <button
          type="button"
          className={'cmt-filter-btn' + (filterAnchor ? ' is-active' : '')}
          onClick={toggleFilter}
          aria-haspopup="dialog"
          aria-expanded={Boolean(filterAnchor)}
        >
          <IconFilter />
          Filter
          {matcher.active ? <span className="cmt-filter-dot" aria-hidden="true" /> : null}
        </button>
        <select
          className="cmt-sort"
          value={filter.sort}
          onChange={(e) => patchFilter({ sort: e.target.value })}
          aria-label="Sort comments"
        >
          {COMMENT_SORTS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="code-comments-scroll">
        {status === 'loading' ? (
          <p className="code-comments-empty">Loading comments…</p>
        ) : status === 'error' ? (
          <p className="code-comments-empty">Could not load comments.</p>
        ) : visible.length === 0 ? (
          <p className="code-comments-empty">
            {comments.length
              ? 'No comments match these filters.'
              : `No comments yet on ${standardName(item?.standard) || 'this document'}. Comments on any item in this document appear here.`}
          </p>
        ) : (
          <div className="cmt-list">
            {visible.map((comment) => (
              <CommentCard
                key={comment._id}
                comment={comment}
                user={user}
                replies={repliesByParent[comment._id]}
                repliesOpen={Boolean(openReplies[comment._id])}
                onToggleReplies={toggleReplies}
                onLike={(c) => toggleLike(c, isLikedBy(c, user?._id), user?._id)}
                onSave={(c) => toggleSave(c, isSavedBy(c, user?._id), user?._id)}
                onShowLikes={showLikes}
                onOpenItem={onOpenItem}
                onUpdate={update}
                onEditItems={editCommentItems}
                onEditTopics={editCommentTopics}
                onRemove={handleRemove}
                onReply={addReply}
                topicOptions={myTopics}
              />
            ))}
          </div>
        )}
      </div>

      <div className="code-comments-composer">
        <CommentComposer
          showItems
          items={composeItems}
          onEditItems={editComposeItems}
          onRemoveItem={(it) =>
            setComposeItems((prev) => prev.filter((x) => x._id !== it._id))
          }
          topicOptions={myTopics}
          onSubmit={({ title, text, isPublic, isQuestion, attachments, guides, topics }) =>
            add({
              title,
              text,
              isPublic,
              isQuestion,
              items: composeItems,
              attachments,
              guides,
              topics,
            })
          }
          placeholder={commentPlaceholder(composeItems)}
        />
      </div>

      {filterAnchor && (
        <CommentsFilterPopover
          anchorEl={filterAnchor}
          filter={filter}
          onPatch={patchFilter}
          onClear={clearFilter}
          onClose={() => setFilterAnchor(null)}
          active={matcher.active}
          authorOptions={authors}
          itemOptions={itemOptions}
          topicOptions={topicOptions}
        />
      )}
    </div>
  )
}
